<script lang="ts">
  interface TimezoneOption {
    title: string;
    value: string | undefined;
    group: string;
    searchTerms?: string;
  }

  import { getTimeZones } from "@vvo/tzdb";
  import { Search } from "lucide-svelte";
  import { createEventDispatcher } from "svelte";

  import DropdownSelect from "./DropdownSelect.svelte";
  import DropdownSelectItem from "./DropdownSelectItem.svelte";

  const autoDetectValue = "Auto-detect (Browser Local)";
  const dispatch = createEventDispatcher<{
    submit: string | undefined;
  }>();

  export let value: string | null;

  let searchQuery = "";
  let dropdownOpen = false;

  $: if (dropdownOpen && (searchQuery || value)) {
    if (searchQuery) {
      const dropdownElement = document.querySelector(".dropdown-body");
      if (dropdownElement) {
        dropdownElement.scrollTo({
          top: 0,
          behavior: "smooth",
        });
      }
    } else {
      const element = document.querySelector(`[data-timezone="${value}"]`);

      if (element) {
        element.scrollIntoView({
          behavior: "auto",
          block: "center",
        });
      }
    }
  }

  const allTimezones = getTimeZones();
  const generalTimezones = [
    { title: autoDetectValue, value: undefined },
    { title: "UTC (UTC+00:00)", value: "Etc/UTC" },
  ];
  const commonTimezones = [
    {
      title: "New York, United States (EST, UTC-05:00)",
      value: "America/New_York",
    },
    {
      title: "Chicago, United States (CST, UTC-06:00)",
      value: "America/Chicago",
    },
    {
      title: "Denver, United States (MST, UTC-07:00)",
      value: "America/Denver",
    },
    {
      title: "Phoenix, United States (MST, UTC-07:00)",
      value: "America/Phoenix",
    },
    {
      title: "Los Angeles, United States (PST, UTC-08:00)",
      value: "America/Los_Angeles",
    },
    {
      title: "Anchorage, United States (AKST, UTC-09:00)",
      value: "America/Anchorage",
    },
    {
      title: "Honolulu, United States (HST, UTC-10:00)",
      value: "Pacific/Honolulu",
    },
    {
      title: "Stockholm, Sweden (CET, UTC+01:00)",
      value: "Europe/Stockholm",
    },
  ];

  const timezones: TimezoneOption[] = [
    ...generalTimezones.map((tz) => ({ ...tz, group: "General" })),
    ...commonTimezones.map((tz) => ({ ...tz, group: "Common" })),
    ...allTimezones
      .filter(
        (tz) =>
          ![...commonTimezones, ...generalTimezones].some(
            (ctz) => ctz.value === tz.name,
          ),
      )
      .map((tz) => {
        const cityCountry =
          tz.mainCities.length > 0
            ? `${tz.mainCities[0]}, ${tz.countryName}`
            : tz.name;
        const offset = tz.currentTimeFormat.split(" ")[1] || "";
        return {
          title: `${cityCountry} (${tz.abbreviation}, ${offset})`,
          value: tz.name,
          group: tz.continentName,
          searchTerms: [
            ...tz.mainCities,
            tz.countryName,
            tz.abbreviation,
            tz.name,
            offset,
          ]
            .filter(Boolean)
            .join(" ")
            .toLowerCase(),
        };
      }),
  ];

  $: filteredTimezones = timezones.filter((tz) => {
    if (!searchQuery) return true;

    const terms = searchQuery.toLowerCase().split(/\s+/).filter(Boolean);
    return terms.every(
      (term) =>
        tz.searchTerms?.includes(term) ||
        tz.title.toLowerCase().includes(term) ||
        tz.value?.toLowerCase().includes(term),
    );
  });

  $: groupedTimezones = filteredTimezones.reduce(
    (acc, tz) => {
      acc[tz.group] = acc[tz.group] || [];
      acc[tz.group].push(tz);
      return acc;
    },
    {} as Record<string, typeof timezones>,
  );
</script>

<DropdownSelect
  value={value || autoDetectValue}
  size="wide"
  on:change={(event) => {
    const isOpen = event.detail;
    if (isOpen) {
      dropdownOpen = true;
      searchQuery = "";
    } else {
      dropdownOpen = false;
    }
  }}
>
  <div
    class="w-[min(calc(100vw-2rem),525px)] max-w-[min(calc(100vw-2rem),525px)] px-2 py-2"
    style="transform-origin: top right"
  >
    <div class="relative">
      <input
        type="text"
        placeholder="Search timezones..."
        class="w-full pl-8 pr-3 py-1 bg-white/5 text-sm rounded-md border border-white/10 placeholder:text-white/30 focus:outline-none focus:border-white/20"
        bind:value={searchQuery}
      />
      <Search size={16} class="absolute left-2 top-1.5 text-white/30" />
    </div>
  </div>

  <div
    class="dropdown-body overflow-y-auto"
    style="max-height: min(calc(100vh - 200px), 200px)"
  >
    {#if filteredTimezones.length === 0}
      <div class="px-4 py-2 text-sm text-white/60">No timezones found</div>
    {:else}
      {#each Object.entries(groupedTimezones) as [group, tzs]}
        {#if group !== "top" && tzs.length}
          <div class="px-4 py-1 text-xs font-medium text-white/40">
            {group}
          </div>
          {#each tzs as timezone}
            <div
              data-timezone={timezone.value || ""}
              class="rounded-md {value === timezone.value ? 'bg-white/5' : ''}"
            >
              <DropdownSelectItem
                title={timezone.title}
                subtitle={timezone.value || ""}
                isActive={value === timezone.value}
                on:click={() => dispatch("submit", timezone.value)}
              />
            </div>
          {/each}
        {/if}
      {/each}
    {/if}
  </div>
</DropdownSelect>
